import {
  Elements,
  PaymentMethodMessagingElement,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useMemo } from 'react';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';

// Initialized outside the component per Stripe's recommendation to avoid
// recreating the promise on every render.
const stripePromise = loadStripe(stripePublicKey);

export type StripeMerchandisingWidgetProps = {
  // Value in cents
  amount: number;
  fontSizeBase?: string;
  textAlign?: 'left' | 'center' | 'right';
  theme?: 'stripe' | 'flat' | 'night' | 'none';
};

/**
 * Renders Stripe's Payment Method Messaging Element.
 * The Payment Method Messaging Element is a UI component for informing a customer about available buy-now-pay-later plans.
 */
const StripeMerchandisingWidget = (props: StripeMerchandisingWidgetProps) => {
  const { amount, fontSizeBase, textAlign = 'center', theme = 'stripe' } = props;

  const appearance = useMemo(() => ({
    theme: theme === 'none' ? undefined : theme,
    variables: {
      fontSizeBase: fontSizeBase || '14px',
    },
    rules: {
      '.PaymentMethodMessaging': {
        textAlign: textAlign,
      },
    },
  }), [theme, fontSizeBase, textAlign]);

  const elementsOptions = useMemo(() => ({ appearance }), [appearance]);

  const messagingOptions = useMemo(() => ({
    amount: Math.round(amount),
    currency: 'USD' as const,
    countryCode: 'US' as const,
    paymentMethodTypes: ['klarna', 'affirm', 'afterpay_clearpay'] as ('klarna' | 'affirm' | 'afterpay_clearpay')[],
  }), [amount]);

  // Validate amount before using it
  // Amount must be a valid positive number in cents
  // This prevents the Stripe error: "amount should be a positive amount in the currency's subunit. You specified: NaN."
  const isValidAmount =
    typeof amount === 'number' &&
    !isNaN(amount) &&
    isFinite(amount) &&
    amount > 0;

  if (!isValidAmount) {
    return null;
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentMethodMessagingElement options={messagingOptions} />
    </Elements>
  );
};

export default StripeMerchandisingWidget;
